import React from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from './WaterSites.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_WATER_SITE_IDS,
  GET_WATER_SITES,
  WaterSiteItem,
} from './WaterSites.query';
import { RouteComponentProps, withRouter } from 'react-router';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import FilterBarWaterSites, {
  defaultFilters,
  WaterSiteFilters,
} from './FilterBarWaterSites';
import useMode from '../../../../hooks/useMode';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import WaterSitesActions from './WaterSitesActions';
import WaterSiteThirdPane from '../../../../components/WaterSite/WaterSiteThirdPane';
import { PortalWithState } from 'react-portal';
import Button from '../../../../components/Button/Button';
import WaterSiteEditModal from '../../../../components/WaterSite/WaterSiteEditModal';
import { getStringSearchOr } from '../../../../helpers/search';
import RequirePerms from '@/components/RequirePerms';

type WaterSiteDetailParams = {
  id?: string;
};

type WaterSiteDetailProps = RouteComponentProps<WaterSiteDetailParams>;

export const searchFields = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'supervisor.name',
    label: 'Supervisor',
  },
  {
    field: 'assignedTo.name',
    label: 'Assignee',
  },
  {
    field: 'address.street1',
    label: 'Address',
  },
  {
    field: 'address.street2',
    label: null,
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: WaterSiteFilters) => {
  return {
    type: storedFilters.type,
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component for water sites page
 */
function WaterSites({ match }: WaterSiteDetailProps): JSX.Element {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<WaterSiteFilters>(
      convertFilters,
      'waterSites',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy('waterSites', availableColumns);

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<WaterSiteItem>(
    'waterSites',
    GET_WATER_SITE_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_WATER_SITES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(
      availableColumns,
      'storedColumnConfigurationWaterSites'
    );

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${useMode()}/water-sites`,
      itemIds,
      match.params.id
    );

  return (
    <ThirdPaneLayout paneWidth={384 * 2} paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Water Sites">
          <RequirePerms waterSites="CREATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    ml={3}
                    variant="primary"
                    icon="plus"
                    onClick={openPortal}
                  >
                    Add Site
                  </Button>
                  {portal(
                    <WaterSiteEditModal
                      action="add"
                      onClose={() => {
                        refetch();
                        closePortal();
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        </PageBar>
        <FilterBarWaterSites
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<WaterSitesActions ids={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <WaterSiteThirdPane
        waterSiteId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

export default withRouter(WaterSites);
